import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { Currency, Locale, customCurrencyMaskConfig } from '@wdx/shared/utils';
import { NgxCurrencyConfig } from 'ngx-currency';
import {
    BehaviorSubject,
    Observable,
    Subject,
    Subscription,
    combineLatest,
} from 'rxjs';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { BaseWdxFormControlClass } from '../../../abstract-classes/base-form-control.class';
import { IFormDynamicData } from '../../../interfaces';
import { ReactiveFormLayoutAndDefinition } from '../../../models';
import * as R from 'ramda';

@Component({
    selector: 'wdx-ff-currency-control',
    templateUrl: './form-currency-control.component.html',
    standalone: false,
})
export class FormCurrencyControlComponent
    extends BaseWdxFormControlClass
    implements OnInit, OnChanges
{
    currencyMaskConfig!: Partial<NgxCurrencyConfig>;
    currencySymbol?: string;
    placeholder!: string;
    currencyFormGroup = new UntypedFormGroup({
        amount: new UntypedFormControl(null),
        isoCode: new UntypedFormControl(null),
    });

    currencies$!: Observable<Currency[]>;
    locale$!: Observable<Locale>;

    get amountControl(): UntypedFormControl {
        return this.currencyFormGroup?.get('amount') as UntypedFormControl;
    }

    get isoCodeControl(): UntypedFormControl {
        return this.currencyFormGroup?.get('isoCode') as UntypedFormControl;
    }

    constructor(
        public override controlContainer: ControlContainer,
        public override dynamicDataService: IFormDynamicData,
    ) {
        super(controlContainer, dynamicDataService);
    }

    private currencyFilter$: BehaviorSubject<string[]> = new BehaviorSubject(
        [] as string[],
    );

    // we need to keep track of the formControl we are subscribing to, as it can change when
    // this control is used in a subform, in that case we need to rescribe to the new formControl
    private formControlSubscribedTo: AbstractControl | undefined;
    private formControlValueChangesSub: Subscription | undefined;

    ngOnInit(): void {
        this.currencyMaskConfig = {
            ...customCurrencyMaskConfig,
            allowNegative: true,
        };

        this.locale$ = this.dynamicDataService.getMeLocale();
        this.currencies$ = combineLatest([
            this.currencyFilter$,
            this.dynamicDataService.getCurrencies(),
        ]).pipe(
            map(([currencyFilter, currencies]) =>
                !currencyFilter?.length ||
                !currencies.some(({ isoCode }) =>
                    currencyFilter.includes(isoCode),
                )
                    ? currencies
                    : currencies.filter(({ isoCode }) =>
                          currencyFilter.includes(isoCode),
                      ),
            ),
        );

        if (this.isDisabled) {
            this.amountControl.disable();
        }

        combineLatest([this.locale$, this.currencies$])
            .pipe(take(1))
            .subscribe(([locale, currencies]) => {
                this.currencyMaskConfig.decimal = locale?.numberFormat
                    ?.numberDecimalSeparator as string;
                this.currencyMaskConfig.thousands = locale?.numberFormat
                    ?.numberGroupSeparator as string;

                this.placeholder = `${Number(0.0).toLocaleString(
                    locale?.code as string,
                    {
                        minimumFractionDigits: 2,
                    },
                )}`;

                const defaultIsoCode =
                    this.formControl?.value?.isoCode ||
                    locale.currency?.isoCode;

                const defaultCurrency = currencies.find(
                    (currency) => currency.isoCode === defaultIsoCode,
                );

                const fallback = currencies[0];

                this.currencySymbol =
                    this.getCurrencySymbol(defaultCurrency) ||
                    this.getCurrencySymbol(fallback);

                this.isoCodeControl.setValue(defaultCurrency ?? fallback);

                if (this.formControl?.value?.amount) {
                    this.amountControl.setValue(
                        this.formControl?.value?.amount,
                    );
                }
            });

        this.amountControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((amount) => {
                this.setValue(amount, this.isoCodeControl.value.isoCode);
                this.formControl?.markAsDirty();
                this.formControl?.markAsTouched();
            });

        this.isoCodeControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((currency) => {
                this.setValue(this.amountControl.value, currency.isoCode);
                this.formControl?.markAsDirty();
                this.currencySymbol = this.getCurrencySymbol(currency);
            });

        this.setupFromControlValueListener();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['formElement'].currentValue) {
            this.setupFromControlValueListener();
            const prevOptions = (
                changes['formElement']
                    .previousValue as ReactiveFormLayoutAndDefinition
            )?.options;
            const options = (
                changes['formElement']
                    .currentValue as ReactiveFormLayoutAndDefinition
            )?.options;

            if (!R.equals(prevOptions, options)) {
                this.currencyFilter$.next((options as string[]) ?? []);
            }
        }
    }

    setValue(amount: number, isoCode: string) {
        this.formControl?.setValue({
            amount,
            isoCode,
        });
    }

    compareSelectedCurrency(a: Currency, b: Currency) {
        return a.isoCode === b.isoCode;
    }

    private getCurrencySymbol(currency?: Currency): string | undefined {
        return currency?.symbol || currency?.isoCode;
    }

    private setupFromControlValueListener() {
        if (this.formControlSubscribedTo === this.formControl) {
            return;
        }
        this.formControlValueChangesSub?.unsubscribe();
        this.formControlSubscribedTo = this.formControl;
        this.formControlValueChangesSub = this.formControl?.valueChanges
            .pipe(
                takeUntil(this.destroyed$),
                startWith(this.formControl?.value),
                filter((value) => Boolean(value) || value === null),
            )
            .subscribe((value) => {
                const amount = value?.amount ?? null;
                if (amount !== this.amountControl.value) {
                    this.amountControl.setValue(amount, {
                        emitEvent: false,
                    });
                }

                if (
                    value?.isoCode &&
                    value.isoCode !== this.isoCodeControl.value?.isoCode
                ) {
                    this.isoCodeControl.setValue(value.isoCode, {
                        emitEvent: false,
                    });
                    this.currencySymbol = value.isoCode;
                }
            });
    }
}
